import { create } from 'zustand';

/**
 * PWA update signal.
 *
 * The service-worker registration (`register-sw.ts`) runs outside React, so it
 * can't render UI directly. When a new service worker finishes installing and
 * is waiting to take over, it flips this store — and `<PwaUpdateBanner>` (mounted
 * at the app root) reacts by showing a persistent "new version available" bar.
 *
 * Why a store and not a one-shot toast: a toast can be swiped away, and the
 * underlying `onNeedRefresh` fires only ONCE per waiting worker — so a dismissed
 * toast is gone until the next deploy. Store state survives an accidental
 * dismiss and re-renders the prompt on every navigation, which is what the
 * "I need to actually see that an update is ready" requirement needs.
 */
interface PwaUpdateState {
  /** True once a new service worker is installed and waiting to activate. */
  updateAvailable: boolean;
  /** Activates the waiting worker and reloads onto the new version. Null until
   *  an update is detected. */
  applyUpdate: (() => void) | null;
  /** Called by the SW registration when a new version is waiting. */
  announceUpdate: (apply: () => void) => void;
}

export const usePwaUpdate = create<PwaUpdateState>((set) => ({
  updateAvailable: false,
  applyUpdate: null,
  announceUpdate: (apply) => set({ updateAvailable: true, applyUpdate: apply }),
}));
